<script setup>
import { computed, nextTick, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { mesmoGrupo } from '../catalogo';

import SeletorDeGrupo from './SeletorDeGrupo.vue';
import EscoposDoModulo from './EscoposDoModulo.vue';

// ACESSO-01 (L9a): a linha de um módulo no editor do papel, como no quadro 2 do mock v3. O nome do módulo, a frase do
// acesso de agora e o seletor do grupo. Os escopos ficam recolhidos e só abrem por "Personalizar escopos" ou, no módulo
// personalizado, por "Ver escopos". O módulo travado não tem seletor: diz "Bloqueado" e o motivo.
//
// O módulo personalizado diz o que mudou em relação ao grupo ("A partir de Constrói, também pode executar flows."), e
// não a frase do grupo: ela não conta o ajuste e às vezes o contradiz ("sem publicar", com o publicar somado). O módulo
// travado diz quem libera antes do motivo, que sozinho dizia o risco ("Mexe no plano…") e não o próximo passo (R7).
const props = defineProps({
  modulo: { type: Object, required: true },
  // Os do módulo, no catálogo.
  grupos: { type: Array, required: true },
  escopos: { type: Array, required: true },
  somenteLeitura: { type: Boolean, default: false },
});

const emit = defineEmits(['criarGrupo']);

const escolha = defineModel('escolha', { type: Object, required: true });
const ajustes = defineModel('ajustes', { type: Array, required: true });

const { t, locale } = useI18n();

const escoposAbertos = ref(false);
const linha = useTemplateRef('linha');

// O botão que abre os escopos some quando eles abrem, e o "Recolher" some quando eles fecham: o foco vai junto, para
// quem usa o teclado não cair no começo da página. Abertos, ele vai à primeira caixa que se marca; recolhidos, ao "Ver
// escopos" do módulo personalizado ou ao seletor.
const focar = seletor =>
  nextTick(() => linha.value?.querySelector(seletor)?.focus());
const abrirEscopos = () => {
  escoposAbertos.value = true;
  focar('input[type="checkbox"]:not(:disabled)');
};
const recolherEscopos = () => {
  escoposAbertos.value = false;
  focar('[data-ver-escopos], [data-seletor]');
};

const grupo = computed(() =>
  props.grupos.find(cada => mesmoGrupo(cada, escolha.value))
);
const personalizado = computed(() => ajustes.value.length > 0);

// A frase do grupo. O da conta não tem: diz quantos escopos ele tem.
const frase = computed(() => {
  if (props.modulo.travado) return props.modulo.motivo;
  if (!grupo.value) return '';
  const n = grupo.value.escopos.length;
  return grupo.value.frase || t('PAPEIS.EDITOR.GRUPO_DA_CONTA', { n }, n);
});

// Os escopos somados ou tirados, pelo rótulo, numa lista da língua de quem lê ("executar flows e publicar flows"). O
// rótulo começa com maiúscula ("Executar flows") e aqui entra no meio da frase.
const listaDos = sentido => {
  const rotulos = ajustes.value
    .filter(ajuste => ajuste.sentido === sentido)
    .map(({ escopo }) => {
      const rotulo =
        props.escopos.find(cada => cada.nome === escopo)?.rotulo || escopo;
      return rotulo.charAt(0).toLocaleLowerCase() + rotulo.slice(1);
    });
  return new Intl.ListFormat(locale.value.replace('_', '-'), {
    type: 'conjunction',
  }).format(rotulos);
};
const fraseDoAjuste = computed(() => {
  const soma = listaDos('soma');
  const tira = listaDos('tira');
  const doGrupo = grupo.value?.rotulo || '';
  if (soma && tira) {
    return t('PAPEIS.EDITOR.AJUSTE.SOMA_E_TIRA', {
      grupo: doGrupo,
      soma,
      tira,
    });
  }
  return soma
    ? t('PAPEIS.EDITOR.AJUSTE.SOMA', { grupo: doGrupo, escopos: soma })
    : t('PAPEIS.EDITOR.AJUSTE.TIRA', { grupo: doGrupo, escopos: tira });
});

// Outro grupo começa sem os ajustes do de antes. O mesmo grupo não muda nada: clicar no que já está marcado só para
// fechar o menu apagava o ajuste sem aviso.
const escolher = nova => {
  if (grupo.value && mesmoGrupo(grupo.value, nova)) return;
  escolha.value = nova;
  ajustes.value = [];
};
</script>

<template>
  <li ref="linha" class="py-3" :data-modulo="modulo.chave">
    <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
      <div class="flex-1 min-w-0">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ modulo.rotulo }}
        </p>
        <p class="mb-0 text-sm text-n-slate-11" data-frase>
          <template v-if="personalizado">
            <span
              class="inline-flex items-center gap-1 font-semibold text-n-slate-12"
              data-personalizado
            >
              <span
                class="i-lucide-settings-2 size-3.5 shrink-0 text-pz-brand-on-tint"
                aria-hidden="true"
              />
              {{ t('PAPEIS.EDITOR.PERSONALIZADO') }}
            </span>
            {{ fraseDoAjuste }}
            <button
              v-if="!escoposAbertos"
              type="button"
              class="p-0 text-sm underline align-baseline bg-transparent rounded text-n-slate-12 decoration-n-strong underline-offset-[3px] hover:decoration-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
              data-ver-escopos
              @click="abrirEscopos"
            >
              {{ t('PAPEIS.EDITOR.VER_ESCOPOS') }}
            </button>
          </template>
          <template v-else-if="modulo.travado">
            <span class="font-semibold text-n-slate-12" data-so-a-powerbizz>
              {{ t('PAPEIS.EDITOR.SO_A_POWERBIZZ') }}
            </span>
            {{ frase }}
          </template>
          <template v-else>{{ frase }}</template>
        </p>
      </div>
      <SeletorDeGrupo
        :modulo="modulo"
        :grupos="grupos"
        :escopos="escopos"
        :escolha="escolha"
        :somente-leitura="somenteLeitura"
        @escolher="escolher"
        @personalizar="abrirEscopos"
        @criar-grupo="emit('criarGrupo', modulo.chave)"
      />
    </div>
    <EscoposDoModulo
      v-if="escoposAbertos && grupo"
      v-model:ajustes="ajustes"
      :escopos="escopos"
      :grupo="grupo"
      @recolher="recolherEscopos"
    />
  </li>
</template>
